import type { CSSProperties, JSX, ReactNode } from "react";
import { cx } from "../cx";

export interface EmptyStateProps {
  /** Decorative lucide icon at 20px (marked aria-hidden by the wrapper). */
  icon?: ReactNode | undefined;
  /** Caps mono eyebrow ("0 rows"). */
  label?: ReactNode | undefined;
  /** Display-font heading ("Nothing observed yet."). */
  title?: ReactNode | undefined;
  /** Action slot (usually one Button). */
  action?: ReactNode | undefined;
  /** Muted body copy. */
  children?: ReactNode | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * Zero state for studio views. Declarative statements, not apologies:
 * "Nothing observed yet." — never "Oops!". Optional icon and mono eyebrow,
 * display-font title, muted description, at most one action. Ported from
 * design/tarski-kit/components/studio/EmptyState.jsx.
 */
export function EmptyState({
  icon,
  label,
  title,
  action,
  children,
  className,
  style,
}: EmptyStateProps): JSX.Element {
  return (
    <div className={cx("tk-empty-state", className)} style={style}>
      <div className="tk-empty-state__inner">
        {icon === undefined ? null : (
          <span className="tk-empty-state__icon" aria-hidden="true">
            {icon}
          </span>
        )}
        {label === undefined ? null : <div className="tk-empty-state__label">{label}</div>}
        {title === undefined ? null : <h3 className="tk-empty-state__title">{title}</h3>}
        {children === undefined ? null : <p className="tk-empty-state__description">{children}</p>}
        {action === undefined ? null : <div className="tk-empty-state__action">{action}</div>}
      </div>
    </div>
  );
}
